import { type InsuranceDto, type InsuranceType, type CreateInsuranceRequest, type UpdateInsuranceRequest, insurancesApi } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const INSURANCE_TYPES: InsuranceType[] = [
  'Brandverzekering',
  'Aansprakelijkheid',
  'Huurwaarborgverzekering',
  'Rechtsbijstand',
];

export function openInsuranceCreateDialog(propertyId: string, onCreated: () => Promise<void>): void {
  openInsuranceDialog(null, propertyId, onCreated);
}

export function openInsuranceEditDialog(insurance: InsuranceDto, onUpdated: () => Promise<void>): void {
  openInsuranceDialog(insurance, insurance.propertyId, onUpdated);
}

function openInsuranceDialog(insurance: InsuranceDto | null, propertyId: string, onDone: () => Promise<void>): void {
  const isEdit = insurance !== null;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${isEdit ? t('insurance.edit') : t('insurance.new')}</h2>
        <button class="modal-close" data-action="cancel">${icon('x', { size: 20 })}</button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${t('common.type')} *</label>
          <select name="type" required>
            ${INSURANCE_TYPES.map((type) => `<option value="${type}" ${isEdit && insurance!.type === type ? 'selected' : ''}>${t(`insurance.type.${type}`)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('insurance.companyName')} *</label>
          <input name="companyName" required placeholder="${t('insurance.companyNamePlaceholder')}" value="${isEdit ? esc(insurance!.companyName) : ''}">
        </div>
        <div class="form-group">
          <label>${t('insurance.policyNumber')} *</label>
          <input name="policyNumber" required placeholder="${t('insurance.policyNumberPlaceholder')}" value="${isEdit ? esc(insurance!.policyNumber) : ''}">
        </div>
        <div class="form-group">
          <label>${t('common.expiryDate')} *</label>
          <input name="expiryDate" type="date" required value="${isEdit ? insurance!.expiryDate : ''}">
        </div>
        <div class="form-group">
          <label>${t('common.notes')} <span class="text-muted">${t('common.optional')}</span></label>
          <textarea name="notes" rows="3" placeholder="${t('insurance.notesPlaceholder')}">${isEdit && insurance!.notes ? esc(insurance!.notes) : ''}</textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${isEdit ? t('common.save') : t('common.add')}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    submitBtn.disabled = true;
    const fd = new FormData(event.currentTarget as HTMLFormElement);
    try {
      if (isEdit) {
        const data: UpdateInsuranceRequest = {
          type: fd.get('type') as InsuranceType,
          companyName: fd.get('companyName') as string,
          policyNumber: fd.get('policyNumber') as string,
          expiryDate: fd.get('expiryDate') as string,
          notes: (fd.get('notes') as string) || undefined,
        };
        await insurancesApi.update(insurance!.id, data);
      } else {
        const data: CreateInsuranceRequest = {
          propertyId,
          type: fd.get('type') as InsuranceType,
          companyName: fd.get('companyName') as string,
          policyNumber: fd.get('policyNumber') as string,
          expiryDate: fd.get('expiryDate') as string,
          notes: (fd.get('notes') as string) || undefined,
        };
        await insurancesApi.create(data);
      }
      close();
      await onDone();
    } catch (error) {
      console.error('Failed to save insurance:', error);
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = error instanceof ApiError ? error.message : t('common.errorGeneric');
      }
      submitBtn.disabled = false;
    }
  });
  document.body.appendChild(overlay);
}
